import { keepPreviousData } from "@tanstack/react-query";
import classNames from "classnames";
import flatten from "lodash/flatten";
import map from "lodash/map";
import { CHAT_NOTIFICATION_CATEGORIES } from "PFApp/constants/notifications";
import { Button } from "PFComponents/button";
import { Stack } from "PFComponents/containers";
import { Divider } from "PFComponents/divider";
import { LoadingDots } from "PFComponents/loading_dots";
import { useNotificationsInfinite } from "PFCore/hooks/queries/notifications/use_notifications";
import { FetchNotificationsPayload } from "PFCore/services/notifications/fetch_notifications";
import { Fragment } from "react";
import { useTranslation } from "react-i18next";

import { useNotificationsPanelContext } from "./contexts/notifications_panel_context";
import { NotificationItem } from "./notification_item";
import css from "./notifications_panel_list.module.scss";
import { NotificationsEmptyState } from "./parts/notifications_empty_state";

type NotificationsPanelListProps = {
  groupFilters?: Pick<FetchNotificationsPayload, "categories" | "engagementId" | "roleId">;
};

export const NotificationsPanelList = ({ groupFilters }: NotificationsPanelListProps) => {
  const { t } = useTranslation("translation");

  const { categories, role, engagement, showUnreadOnly, showImportantOnly } = useNotificationsPanelContext();

  const params = {
    perPage: 25,
    read: showUnreadOnly ? false : undefined,
    important: showImportantOnly ? true : undefined,
    categories: categories.length ? categories : undefined,
    excludedCategories: CHAT_NOTIFICATION_CATEGORIES,
    engagementId: engagement ? Number(engagement?.id) : undefined,
    roleId: role ? Number(role?.id) : undefined,
    ...(groupFilters || {})
  };

  const {
    data: notificationPages,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
    fetchNextPage
  } = useNotificationsInfinite(params, {
    placeholderData: keepPreviousData
  });

  const entries = flatten(map(notificationPages?.pages, "entries"));

  return (
    <Stack className={classNames(css.list, { [css.isLoading]: isLoading })}>
      {map(entries, (notification, index) => (
        <Fragment key={notification.id}>
          <NotificationItem notification={notification} />
          {index < entries.length - 1 && <Divider />}
        </Fragment>
      ))}

      <Stack center>
        {entries.length === 0 && isLoading && <LoadingDots circlesEnabled circleSize="sm" />}
        {entries.length === 0 && !isLoading && <NotificationsEmptyState />}
        {hasNextPage && !isFetchingNextPage && (
          <Button kind="text" onClick={() => fetchNextPage()}>
            {t("notifications.showOlder")}
          </Button>
        )}
        {isFetchingNextPage && <LoadingDots circlesEnabled circleSize="sm" />}
      </Stack>
    </Stack>
  );
};
